import React, { useState, useEffect, useRef } from 'react';
import Axios from "axios";
import { Download, ChevronLeft, ChevronRight, Loader2, Search, ChevronsUpDown, Check, RotateCcw, ExternalLink, AlertTriangle, AlertCircle } from "lucide-react";
import CsvUploadModal from "./CsvUploadModal";
import RatesDisplay from "./RatesDisplay";

const Card = ({ children, className = "" }) => (
  <div className={`bg-white rounded-lg shadow-sm ${className}`}>
    {children}
  </div>
);

const REQUIRED_COLUMNS = [
  'Grouped trip',
  'Trip km',
  'Vehicle car rate',
  'Passenger fare',
  'Tier',
  'Short fall',
  'cancelled'
];

const CsvCostValidator = () => {

  const [isLoadingCompanies, setIsLoadingCompanies] = useState(true);
  const [isProcessingCsv, setIsProcessingCsv] = useState(false);
  const [csvData, setCsvData] = useState([]);
  const [validationResults, setValidationResults] = useState([]);
  const [error, setError] = useState("");
  const [selectedCompany, setSelectedCompany] = useState("");
  const [companies, setCompanies] = useState({});
  const [rateRanges, setRateRanges] = useState({});

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const [sortOrder, setSortOrder] = useState('all');
  const [groupIdSearch, setGroupIdSearch] = useState("");

  const getSortedResults = () => {
    let filtered = [...validationResults];

    // First apply group ID filter if there's a search term
    if (groupIdSearch) {
      filtered = filtered.filter(result =>
        result.groupId.toLowerCase().includes(groupIdSearch.toLowerCase())
      );
    }

    // Then apply the sort order filter
    switch (sortOrder) {
      case 'correct':
        filtered = filtered.filter(result => result.isRateCorrect && result.isShortfallCorrect);
        break;
      case 'invalid':
        filtered = filtered.filter(result => !result.isRateCorrect || !result.isShortfallCorrect);
        break;
      default:
        break;
    }
    return filtered;
  };

  // Reset function to clear all data and filters
  const handleReset = () => {
    setCsvData([]);
    setValidationResults([]);
    setCurrentPage(1);
    setError("");
    setSortOrder('all');
    setGroupIdSearch("");
    // Reset file input
    const fileInput = document.querySelector('input[type="file"]');
    if (fileInput) {
      fileInput.value = "";
    }
  };


  // Calculate pagination values
  const sortedResults = getSortedResults();
  const totalPages = Math.ceil(sortedResults.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentResults = sortedResults.slice(startIndex, endIndex);

  // Loading Spinner Component
  const LoadingSpinner = ({ className = "h-4 w-4" }) => (
    <Loader2 className={`animate-spin ${className}`} />
  );

  const downloadResults = () => {
    // First, map the original CSV data with validation results
    const enrichedData = csvData.map(row => {
      // Find the validation result for this row's group
      const validationResult = validationResults.find(r => r.groupId === row['Grouped trip']);

      // ⚠️ Determine what's incorrect (if anything)
      let issues = [];
      if (validationResult) {
        if (!validationResult.isRateCorrect) issues.push('Incorrect Rate');
        if (!validationResult.isShortfallCorrect) issues.push('Incorrect Shortfall');
      }

      // Create a new object with all original data plus validation info
      return {
        ...row,
        'Validation Status': issues.length ? issues.join(', ') : 'Correct',
        'Expected Rate': validationResult ? formatCurrency(validationResult.expectedRate).replace('R', '') : '',
        'Correct Shortfall': validationResult ? formatCurrency(validationResult.correctShortfall).replace('R', '') : ''
      };
    });

    // Get all headers from the first row plus our new columns
    const originalHeaders = Object.keys(csvData[0] || {});
    const headers = [
      ...originalHeaders,
      'Validation Status',
      'Expected Rate',
      'Correct Shortfall'
    ];

    // Convert to CSV format
    const csvRows = enrichedData.map(row =>
      headers.map(header => {
        const value = row[header] || '';
        // Wrap values containing commas in quotes
        return value.toString().includes(',') ? `"${value}"` : value;
      }).join(',')
    );

    const csvContent = [headers.join(','), ...csvRows].join('\n');

    // Create and trigger download
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `trip-validation-results-${selectedCompany}-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const getCarType = (vehicleCarRate) => {
    if (!vehicleCarRate) return "PREMIUM_SEDAN";

    // Convert to uppercase for case-insensitive comparison
    const rateType = vehicleCarRate.trim().toUpperCase();

    // Map vehicle car rates to rate table vehicle types
    if (rateType.includes('VAN')) {
        return 'VAN';
    } else if (rateType.includes('MINIBUS')) {
        return 'MINIBUS';
    } else if (rateType.includes('EXECUTIVE')) {
        return 'EXECUTIVE_SEDAN';
    } else if (rateType.includes('PREMIUM')) {
        return 'PREMIUM_SEDAN';
    }

    // Default to PREMIUM_SEDAN if no valid vehicle type is found
    return "PREMIUM_SEDAN";
};

  // Process billing data into rate ranges
  const processRateRanges = (companyData) => {
    const rates = {};

    companyData.billings?.forEach(billing => {
      const carType = billing.car_type.toUpperCase().replace(/\s+/g, '_');

      // Only process if rates are valid (greater than 1)
      if (billing.km0_km19 > 1) {
        rates[carType] = {
          km0_km19: { rate: parseFloat(billing.km0_km19), max: 19 },
          km20_km29: { rate: parseFloat(billing.km20_km29), max: 29 },
          km30_km39: { rate: parseFloat(billing.km30_km39), max: 39 },
          km40_km49: { rate: parseFloat(billing.km40_km49), max: 49 },
          km50_km59: { rate: parseFloat(billing.km50_km59), max: 59 },
          km60_km69: { rate: parseFloat(billing.km60_km69), max: 69 },
          km70_km79: { rate: parseFloat(billing.km70_km79), max: 79 },
          km80_km89: { rate: parseFloat(billing.km80_km89), max: 89 },
          km90: { rate: parseFloat(billing.km90), max: Infinity },
          app_fee: parseFloat(billing.app_fee || 0),
          // Use actual values even if 0
          cancelled: parseFloat(billing.cancelled || 0),
          vat_percentage: parseFloat(billing.vat_percentage || 0)
        };

      }
    });

    return rates;
  };

  // Get rate for specific company and distance
  const getRateForDistance = (distance, vehicleType) => {
    if (!rateRanges[selectedCompany] || !rateRanges[selectedCompany][vehicleType]) {
      console.warn(`No rates found for company ${selectedCompany} and vehicle type ${vehicleType}`);
      return 0;
    }

    const baseRates = rateRanges[selectedCompany][vehicleType];
    let rate = 0;

    // Find the appropriate rate for the distance
    if (distance <= 19) {
      rate = baseRates.km0_km19?.rate;
    } else if (distance <= 29) {
      rate = baseRates.km20_km29?.rate;
    } else if (distance <= 39) {
      rate = baseRates.km30_km39?.rate;
    } else if (distance <= 49) {
      rate = baseRates.km40_km49?.rate;
    } else if (distance <= 59) {
      rate = baseRates.km50_km59?.rate;
    } else if (distance <= 69) {
      rate = baseRates.km60_km69?.rate;
    } else if (distance <= 79) {
      rate = baseRates.km70_km79?.rate;
    } else if (distance <= 89) {
      rate = baseRates.km80_km89?.rate;
    } else {
      rate = baseRates.km90?.rate;
    }

    return rate || 0;
  };

  const calculateSummary = (results) => {
    if (!results.length) return {
      totalTrips: 0,
      totalKm: 0,
      vanTrips: 0,
      sedanTrips: 0
    };

    return results.reduce((summary, result) => ({
      totalTrips: summary.totalTrips + 1,
      totalKm: summary.totalKm + (parseFloat(result.tripKm) || 0),
      incorrectRates: summary.incorrectRates + (result.isRateCorrect ? 0 : 1),
      incorrectShortfalls: summary.incorrectShortfalls + (result.isShortfallCorrect ? 0 : 1),
      vanTrips: summary.vanTrips + (result.tripType?.includes('VAN') ? 1 : 0),
      sedanTrips: summary.sedanTrips + (!result.tripType?.includes('VAN') ? 1 : 0),
    }), {
      totalTrips: 0,
      totalKm: 0,
      incorrectRates: 0,
      incorrectShortfalls: 0,
      vanTrips: 0,
      sedanTrips: 0
    });
  };

  const formatCurrency = (value) => {
    if (isNaN(value) || value === null || value === undefined) return "-";
    return `R${value.toFixed(2)}`;
  };

  const formatNumber = (value) => {
    if (isNaN(value) || value === null || value === undefined) return "-";
    return value.toFixed(2);
  };


  const validateTrips = async (data) => {
    if (!data?.length) return;

    setIsProcessingCsv(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 500));

      const groupedTrips = data.reduce((acc, trip) => {
        const id = trip["Grouped trip"];
        if (!id) return acc;
        if (!acc[id]) acc[id] = [];
        acc[id].push(trip);
        return acc;
      }, {});

      const results = Object.entries(groupedTrips).map(([groupId, trips]) => {
        const passengerFare = parseFloat(String(trips[0]["Passenger fare"]).replace(/[^\d.-]/g, '')) || 0;
        const tier = parseFloat(String(trips[0]["Tier"]).replace(/[^\d.-]/g, '')) || 0;
        const isNoTrip = passengerFare === 0 && tier === 0;

        const tripKmRaw = trips[0]["Trip km"] || trips[0]["Rounded trip km"] || "0";
        const tripKm = isNoTrip ? "No Trip" : (parseFloat(tripKmRaw.replace(/[^\d.-]/g, '')) || 0);
        const vehicleType = getCarType(trips[0]["Vehicle car rate"]);
        const isCancelled = trips[0]["cancelled"] === "1";

        // Set expected rate to 0 for no-trip cases, otherwise calculate normally
        let expectedRate = 0;
        if (!isNoTrip) {
          // For cancelled trips, use flat rate of 150
          const baseRate = isCancelled ? rateRanges[selectedCompany]?.[vehicleType]?.cancelled : getRateForDistance(tripKm, vehicleType);
          const appFee = parseFloat(rateRanges[selectedCompany]?.[vehicleType]?.app_fee) || 0;
          expectedRate = parseFloat((baseRate + appFee).toFixed(2));
        }

        const totalFares = parseFloat(
          trips.reduce(
            (sum, trip) => sum + (parseFloat(String(trip["Passenger fare"]).replace(/[^\d.-]/g, '')) || 0),
            0
          ).toFixed(2)
        );

        const currentShortfall = parseFloat(String(trips[0]["Short fall"]).replace(/[^\d.-]/g, '')) || 0;

        // Calculate correct shortfall with proper decimal handling
        let correctShortfall = parseFloat((expectedRate - totalFares).toFixed(2));

        // Update validation logic for shortfalls
        // A shortfall is considered correct if it's within 0.02 of the correct value
        const isShortfallCorrect = Math.abs(currentShortfall - correctShortfall) <= 0.02;

        // Rate validation remains the same
        const isRateCorrect = Math.abs(expectedRate - (totalFares + currentShortfall)) < 0.01;

        return {
          groupId,
          tripKm,
          tripType: vehicleType,
          expectedRate,
          totalFares,
          currentShortfall,
          correctShortfall,
          isRateCorrect,
          isShortfallCorrect,
          isCancelled,
          isNoTrip
        };
      });

      setValidationResults(results);
    } catch (error) {
      console.error("Error validating trips:", error);
      setError("Error processing CSV data");
    } finally {
      setIsProcessingCsv(false);
    }
  };


  const handleFileUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsProcessingCsv(true);
    const reader = new FileReader();

    reader.onload = async (e) => {
      try {
        const text = e.target.result;
        if (!text) throw new Error("File is empty");

        const rows = text.split("\n");
        if (rows.length < 2) throw new Error("File must contain headers and data");

        const headers = rows[0].split(",").map((h) => h.trim());
        if (!headers.length) throw new Error("No headers found in file");

        // Validate required columns case-insensitively
        const missingColumns = REQUIRED_COLUMNS.filter(
          col => !headers.some(h => h.toLowerCase() === col.toLowerCase())
        );

        if (missingColumns.length > 0) {
          throw new Error(`Missing required columns: ${missingColumns.join(", ")}`);
        }

        const parsedData = rows
          .slice(1)
          .filter((row) => row.trim())
          .map((row) => {
            const values = row.split(/,(?=(?:(?:[^"]*"){2})*[^"]*$)/);
            return headers.reduce((obj, header, index) => {
              let value = values[index] || "";
              value = value.replace(/^["']|["']$/g, '').trim();
              obj[header] = value;
              return obj;
            }, {});
          });

        setCsvData(parsedData);
        await validateTrips(parsedData);
        setError("");
      } catch (err) {
        console.error("CSV parsing error:", err);
        setError(`Error processing file: ${err.message}`);
        setCsvData([]);
        setValidationResults([]);
      } finally {
        setIsProcessingCsv(false);
      }
    };

    reader.onerror = () => {
      setError("Error reading file");
      setIsProcessingCsv(false);
    };

    reader.readAsText(file);
  };

  const CompanySelector = ({ companies, selectedCompany, onCompanySelect }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const dropdownRef = useRef(null);

    const filteredCompanies = Object.keys(companies).filter(company =>
      company.toLowerCase().includes(searchTerm.toLowerCase())
    ).sort((a, b) => a.localeCompare(b));

    const handleSearchClick = (e) => {
      e.stopPropagation();
      setIsOpen(true);
    };

    const handleCompanySelect = (company) => {
      onCompanySelect(company);
      setSearchTerm('');
      setIsOpen(false);
    };

    return (
      <div className="relative" ref={dropdownRef}>
        <div
          className="relative flex items-center justify-between w-full p-3 text-left bg-white border border-gray-300 rounded-lg cursor-pointer hover:border-gray-400 focus-within:border-[#745d96] focus-within:ring-2 focus-within:ring-purple-200 transition-all"
          onClick={() => setIsOpen(!isOpen)}
        >
          <div className="flex items-center flex-1 min-w-0">
            <Search className="h-4 w-4 text-gray-400 flex-shrink-0 mr-2" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onClick={handleSearchClick}
              placeholder={selectedCompany || "Search companies..."}
              className="w-full bg-transparent border-none focus:outline-none focus:ring-0 placeholder-gray-400 truncate"
            />
          </div>
          <ChevronsUpDown className="h-4 w-4 text-gray-400 flex-shrink-0 ml-2" />
        </div>

        {isOpen && (
          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg h-48 overflow-y-auto">
            <div className="">
              {filteredCompanies.length > 0 ? (
                filteredCompanies.map((company) => (
                  <button
                    key={company}
                    onClick={() => handleCompanySelect(company)}
                    className="flex items-center w-full px-4 py-2.5 text-left hover:bg-gray-50 focus:bg-gray-50 focus:outline-none space-x-2"
                  >
                    <span className={`flex-1 truncate ${selectedCompany === company ? 'font-medium text-purple-600' : 'text-gray-700'
                      }`}>
                      {company}
                    </span>
                    {selectedCompany === company && (
                      <Check className="h-4 w-4 text-purple-600 flex-shrink-0" />
                    )}
                  </button>
                ))
              ) : (
                <div className="px-4 py-2.5 text-sm text-gray-500">
                  No companies found
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  useEffect(() => {
    const fetchCompanyData = async () => {
      setIsLoadingCompanies(true);
      try {
        const response = await Axios.get("/csv_data_checker/billings", {
          headers: { Accept: "application/json" }
        });

        const data = response.data.companies;
        const processedCompanies = {};
        const processedRates = {};

        Object.entries(data).forEach(([id, companyData]) => {
          if (companyData?.company_name) {
            processedCompanies[companyData.company_name] = { id };
            processedRates[companyData.company_name] = processRateRanges(companyData);
          }
        });

        setCompanies(processedCompanies);
        setRateRanges(processedRates);

        if (!selectedCompany && Object.keys(processedCompanies)[0]) {
          setSelectedCompany(Object.keys(processedCompanies)[0]);
        }
      } catch (error) {
        console.error("Error fetching company data:", error);
        setError("Failed to load company data. Please try again later.");
      } finally {
        setIsLoadingCompanies(false);
      }
    };

    fetchCompanyData();
  }, []);

  const PageControls = () => {
    // Calculate the range of page numbers to show
    const getPageNumbers = () => {
      const current = currentPage;
      const numbers = [];

      if (totalPages <= 7) {
        // Show all pages if total pages are 7 or less
        for (let i = 1; i <= totalPages; i++) {
          numbers.push(i);
        }
      } else {
        // Always include first page
        numbers.push(1);

        if (current <= 3) {
          // If current page is near the start
          numbers.push(2, 3, 4, '...', totalPages - 1, totalPages);
        } else if (current >= totalPages - 2) {
          // If current page is near the end
          numbers.push('...', totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
        } else {
          // If current page is in the middle
          numbers.push('...', current - 1, current, current + 1, '...', totalPages);
        }
      }

      return numbers;
    };

    return (
      <div className="flex flex-col items-center gap-4 py-4">
        {/* Items per page selector */}
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600">Rows per page:</span>
          <select
            value={itemsPerPage}
            onChange={(e) => {
              setItemsPerPage(Number(e.target.value));
              setCurrentPage(1);
            }}
            className="p-2 border border-gray-200 rounded-lg text-sm bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#745d96] focus:border-[#745d96]"
          >
            <option value={5}>5</option>
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </div>

        {/* Pagination controls */}
        <div className="flex items-center justify-center space-x-2">
          <button
            onClick={() => setCurrentPage(page => Math.max(1, page - 1))}
            disabled={currentPage === 1}
            className="p-2 rounded-lg border border-gray-200 text-gray-400 hover:bg-gray-50 disabled:opacity-50 disabled:hover:bg-white"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>

          {getPageNumbers().map((number, index) => (
            <button
              key={index}
              onClick={() => typeof number === 'number' ? setCurrentPage(number) : null}
              disabled={typeof number !== 'number'}
              className={`w-10 h-10 flex items-center justify-center rounded-lg ${currentPage === number
                ? 'bg-purple-600 text-white'
                : typeof number === 'number'
                  ? 'border border-gray-200 hover:bg-gray-50'
                  : 'border-none'
                }`}
            >
              {number}
            </button>
          ))}

          <button
            onClick={() => setCurrentPage(page => Math.min(totalPages, page + 1))}
            disabled={currentPage === totalPages}
            className="p-2 rounded-lg border border-gray-200 text-gray-400 hover:bg-gray-50 disabled:opacity-50 disabled:hover:bg-white"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>

        {/* Total items display */}
        <div className="text-sm text-gray-600">
          Showing {startIndex + 1} to {Math.min(endIndex, validationResults.length)} of {validationResults.length} entries
        </div>
      </div>
    );
  };

  const handleCompanySelect = (company) => {
    setSelectedCompany(company);
    // Reset all relevant state
    setCsvData([]);
    setValidationResults([]);
    setCurrentPage(1);
    setError("");
    setSortOrder('all');
    setGroupIdSearch("");
    // Reset file input
    const fileInput = document.querySelector('input[type="file"]');
    if (fileInput) {
      fileInput.value = "";
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-8 w-full">
      <div className="max-w-7xl mx-auto space-y-8 w-full">
        {/* Header Section */}
        <div className="bg-white rounded-lg shadow-sm p-6 flex justify-between items-center w-full">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Trip Cost Validator</h1>
            <p className="text-gray-500 mt-1">Validate and analyze trip costs</p>
          </div>

          <div className='inline-flex gap-4'>
            <CsvUploadModal
              onFileUpload={handleFileUpload}
              isProcessing={isProcessingCsv}
              error={error}
              validationResults={validationResults}
            />

            {validationResults.length > 0 && !isProcessingCsv && (
              <>
                <button
                  onClick={downloadResults}
                  className="inline-flex items-center px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#745d96] transition-colors"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Export Results
                </button>
                <button
                  onClick={handleReset}
                  className="inline-flex items-center px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors"
                >
                  <RotateCcw className="h-4 w-4 mr-2" />
                  Reset
                </button>
              </>
            )}
          </div>
        </div>

        {/* Company Selection & Rates Display */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Card className="lg:col-span-1 p-6">
            <div className="space-y-4">
              <h2 className="text-lg text-purple-600 font-semibold">
                {selectedCompany ? `${selectedCompany}` : "Company Billings"}
              </h2>
              <div className="company-selector">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Company
                </label>
                {isLoadingCompanies ? (
                  <div className="flex items-center justify-center p-4">
                    <LoadingSpinner className="h-6 w-6 text-purple-600" />
                  </div>
                ) : (
                  <CompanySelector
                    companies={companies}
                    selectedCompany={selectedCompany}
                    onCompanySelect={handleCompanySelect}
                  />
                )}
              </div>
            </div>
          </Card>

          <Card className="lg:col-span-2 p-6">
            <div className="space-y-4">
              <h2 className="text-lg font-semibold">Current Rates</h2>
              {isLoadingCompanies ? (
                <div className="flex items-center justify-center p-8">
                  <LoadingSpinner className="h-8 w-8 text-purple-600" />
                </div>
              ) : (
                <RatesDisplay
                  rates={rateRanges[selectedCompany] || {}}
                  isLoading={isLoadingCompanies}
                  formatCurrency={formatCurrency}
                />
              )}
            </div>
          </Card>
        </div>

        {error && (
          <Card className="border border-red-200 bg-red-50">
            <div className="p-6">
              <div className="flex items-start">
                <AlertCircle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800">
                    CSV Validation Error
                  </h3>
                  <div className="mt-2">
                    {error.includes("Missing required columns") ? (
                      <>
                        <p className="text-sm text-red-700">
                          Your CSV file is missing some required columns:
                        </p>
                        <ul className="mt-2 list-disc list-inside text-sm text-red-700 space-y-1">
                          {error
                            .replace("Error processing file: Missing required columns: ", "")
                            .split(", ")
                            .map((column) => (
                              <li key={column} className="ml-2">
                                <code className="px-1 py-0.5 bg-red-100 rounded text-red-800">
                                  {column}
                                </code>
                              </li>
                            ))}
                        </ul>
                        <div className="mt-4 flex items-start space-x-2">
                          <AlertTriangle className="h-4 w-4 text-red-600 mt-1 flex-shrink-0" />
                          <div className="text-sm text-red-700">
                            <p className="font-medium">Required Action:</p>
                            <p>Please ensure your CSV file includes all required columns and try uploading again.</p>
                          </div>
                        </div>
                      </>
                    ) : (
                      <p className="text-sm text-red-700">{error}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </Card>
        )}

        {validationResults.length > 0 && (
          <div className="space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {(() => {
                const summary = calculateSummary(validationResults);
                return (
                  <>
                    <Card className="p-6">
                      <div className="space-y-4">
                        <h3 className="text-lg font-semibold">Trip Summary</h3>
                        <dl className="grid grid-cols-2 gap-2 text-sm">
                          <dt className="text-gray-500">Total Trips:</dt>
                          <dd className="text-right font-medium">{summary.totalTrips}</dd>
                          <dt className="text-gray-500">Van Trips:</dt>
                          <dd className="text-right font-medium">{summary.vanTrips}</dd>
                          <dt className="text-gray-500">Sedan Trips:</dt>
                          <dd className="text-right font-medium">{summary.sedanTrips}</dd>
                          <dt className="text-gray-500">Total KM:</dt>
                          <dd className="text-right font-medium">
                            {formatNumber(summary.totalKm)} km
                          </dd>
                        </dl>
                      </div>
                    </Card>

                    <Card className="p-6">
                      <div className="space-y-4">
                        <h3 className="text-lg font-semibold">Issues Found</h3>
                        <dl className="grid grid-cols-2 gap-2 text-sm">
                          {/* <dt className="text-gray-500">Rate Issues:</dt> */}
                          {/* <dd className="text-right font-medium text-red-600">
                            {summary.incorrectRates}
                          </dd> */}
                          <dt className="text-gray-500">Shortfall Issues:</dt>
                          <dd className="text-right font-medium text-red-600">
                            {summary.incorrectShortfalls}
                          </dd>
                          {/* <dt className="text-gray-500">Total Issues:</dt> */}
                          {/* <dd className="text-right font-medium text-red-600">
                            {summary.incorrectRates + summary.incorrectShortfalls}
                          </dd> */}
                        </dl>
                      </div>
                    </Card>

                    <Card className="p-6">
                      <div className="space-y-4">
                        <h3 className="text-lg font-semibold">Averages</h3>
                        <dl className="grid grid-cols-2 gap-2 text-sm">
                          <dt className="text-gray-500">Avg Distance:</dt>
                          <dd className="text-right font-medium">
                            {formatNumber(summary.totalTrips ? summary.totalKm / summary.totalTrips : 0)} km
                          </dd>
                          {/* <dt className="text-gray-500">Avg Revenue:</dt>
                          <dd className="text-right font-medium">
                            {formatCurrency(summary.totalTrips ? summary.totalFares / summary.totalTrips : 0)}
                          </dd> */}
                          {/* <dt className="text-gray-500">Revenue/KM:</dt>
                          <dd className="text-right font-medium">
                            {formatCurrency(summary.totalKm ? summary.totalFares / summary.totalKm : 0)}
                          </dd> */}
                        </dl>
                      </div>
                    </Card>
                  </>
                );
              })()}
            </div>

            {/* Results Table */}
            <Card className="p-6">
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-semibold">Validation Results</h3>
                  <div className='flex gap-3'>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Search className="h-4 w-4 text-gray-400" />
                      </div>
                      <input
                        type="text"
                        value={groupIdSearch}
                        onChange={(e) => {
                          setGroupIdSearch(e.target.value);
                          setCurrentPage(1); // Reset to first page when searching
                        }}
                        placeholder="Search Group ID..."
                        className="pl-10 p-2 border border-gray-200 rounded-lg text-sm bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#745d96] focus:border-[#745d96] w-64"
                      />
                    </div>
                    <select
                      value={sortOrder}
                      onChange={(e) => {
                        setSortOrder(e.target.value);
                        setCurrentPage(1);
                      }}
                      className="p-2 border border-gray-200 rounded-lg text-sm bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#745d96] focus:border-[#745d96]"
                    >
                      <option value="all">All Results</option>
                      <option value="correct">Correct Only</option>
                      <option value="invalid">Invalid Only</option>
                    </select>
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Group ID
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Trip KM
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Trip Type
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Expected Rate
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Total Fares
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Current Shortfall
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Correct Shortfall
                        </th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Action
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {currentResults.map((result) => (
                        <tr key={result.groupId} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm text-gray-900">
                            {result.groupId}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900 text-right">
                            {typeof result.tripKm === 'number' ? result.tripKm.toFixed(2) : result.tripKm}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900">
                            {result.tripType}
                          </td>
                          <td className={`px-4 py-3 text-sm text-right ${!result.isRateCorrect ? "bg-yellow-50" : ""
                            }`}>
                            {formatCurrency(result.expectedRate)}
                          </td>
                          <td className="px-4 py-3 text-sm text-right">
                            {formatCurrency(result.totalFares)}
                          </td>
                          <td className={`px-4 py-3 text-sm text-right ${!result.isShortfallCorrect ? "bg-red-50" : ""
                            }`}>
                            {formatCurrency(result.currentShortfall)}
                          </td>
                          <td className={`px-4 py-3 text-sm text-right ${!result.isShortfallCorrect ? "bg-green-50" : ""
                            }`}>
                            {formatCurrency(result.correctShortfall)}
                          </td>
                          <td className="px-4 py-3 text-center">
                            {result.isRateCorrect && result.isShortfallCorrect ? (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                Correct
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                Invalid
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm">
                            <button
                              onClick={() => window.open(`/grouped_trips/${result.groupId}`, '_blank')}
                              className="inline-flex items-center px-2 py-1 text-xs font-medium text-purple-600 hover:text-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                            >
                              <ExternalLink className="h-3 w-3 mr-1" />
                              View
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <PageControls />
              </div>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default CsvCostValidator;
